import React, { useEffect, useRef } from "react";

// Ambient backdrop: nodes drift slowly and link to nearby neighbors, with
// small signal pulses traveling along the active links — a network topology
// standing in for the site's subject (fiber/network engineering) instead of
// decorative shapes. Deliberately low-alpha so it stays background texture,
// not foreground motion; reads from the theme's accent color so it matches
// both the dark and light palettes without its own color choices.
const NODE_COUNT_DESKTOP = 64;
const NODE_COUNT_MOBILE = 34;
const LINK_DISTANCE = 195;
const SIGNAL_SPAWN_CHANCE = 0.03;
const MAX_ACTIVE_SIGNALS = 9;
const FALLBACK_ACCENT_RGB = "78, 121, 242";

const hexToRgb = (hex) => {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) return null;
  const [, r, g, b] = match;
  return `${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}`;
};

const NetworkBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let width = 0;
    let height = 0;
    let nodes = [];
    let signals = [];
    let frameId;
    let accentRgb = FALLBACK_ACCENT_RGB;

    const readAccentColor = () => {
      const value = getComputedStyle(document.documentElement)
        .getPropertyValue("--accent")
        .trim();
      accentRgb = hexToRgb(value) || accentRgb;
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const createNodes = () => {
      const count = width < 640 ? NODE_COUNT_MOBILE : NODE_COUNT_DESKTOP;
      const speed = prefersReducedMotion ? 0 : 0.15;
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * speed,
        vy: (Math.random() - 0.5) * speed,
      }));
      signals = [];
    };

    const maybeSpawnSignal = () => {
      if (prefersReducedMotion) return;
      if (signals.length >= MAX_ACTIVE_SIGNALS) return;
      if (Math.random() > SIGNAL_SPAWN_CHANCE) return;
      const a = nodes[Math.floor(Math.random() * nodes.length)];
      const b = nodes[Math.floor(Math.random() * nodes.length)];
      if (a === b) return;
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (dist < LINK_DISTANCE) signals.push({ a, b, t: 0 });
    };

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      for (const node of nodes) {
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;
      }

      ctx.lineWidth = 1.3;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist < LINK_DISTANCE) {
            const alpha = (1 - dist / LINK_DISTANCE) * 0.42;
            ctx.strokeStyle = `rgba(${accentRgb}, ${alpha})`;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      for (const node of nodes) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, 2.6, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${accentRgb}, 0.62)`;
        ctx.fill();
      }

      maybeSpawnSignal();
      signals = signals.filter((signal) => signal.t < 1);
      for (const signal of signals) {
        signal.t += 0.012;
        const x = signal.a.x + (signal.b.x - signal.a.x) * signal.t;
        const y = signal.a.y + (signal.b.y - signal.a.y) * signal.t;
        const pulse = Math.sin(signal.t * Math.PI);
        ctx.beginPath();
        ctx.arc(x, y, 3.3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${accentRgb}, ${0.95 * pulse})`;
        ctx.shadowColor = `rgba(${accentRgb}, ${0.9 * pulse})`;
        ctx.shadowBlur = 12;
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    };

    const tick = () => {
      render();
      if (!prefersReducedMotion) frameId = requestAnimationFrame(tick);
    };

    const handleResize = () => {
      resize();
      createNodes();
    };

    resize();
    createNodes();
    readAccentColor();
    tick();

    const themeObserver = new MutationObserver(readAccentColor);
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
      themeObserver.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className="w-full h-full block" aria-hidden="true" />;
};

export default NetworkBackground;
